"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";

export const HEADER_DAILY_GAME_PATH = "/daily";
const DAILY_PUZZLE_LABEL = "Daily Puzzle";

/** Gradient shine + per-letter hop on the Daily Puzzle nav link. */
export const HEADER_DAILY_NAV_GRADIENT_ANIMATION_ENABLED = true;

const linkFocusClass =
  "whitespace-nowrap font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded px-1";

export function HeaderDailyNavLink({ className }: { className?: string }) {
  const pathname = usePathname();
  const isCurrent = pathname === HEADER_DAILY_GAME_PATH;

  if (!HEADER_DAILY_NAV_GRADIENT_ANIMATION_ENABLED) {
    return (
      <Link
        href={HEADER_DAILY_GAME_PATH}
        className={cn("text-white hover:text-gray-300", linkFocusClass, className)}
        aria-current={isCurrent ? "page" : undefined}
      >
        {DAILY_PUZZLE_LABEL}
      </Link>
    );
  }

  return (
    <Link
      href={HEADER_DAILY_GAME_PATH}
      className={cn(
        "pm-daily-nav-gradient-root",
        linkFocusClass,
        /* Baseline-align like plain text links; avoid align-middle on nested inline-blocks (pushes glyphs down). */
        "inline-flex flex-nowrap items-baseline gap-0",
        className
      )}
      aria-current={isCurrent ? "page" : undefined}
    >
      <span className="sr-only">{DAILY_PUZZLE_LABEL}</span>
      <span aria-hidden="true" className="inline-flex flex-nowrap items-baseline whitespace-pre">
        {Array.from(DAILY_PUZZLE_LABEL).map((character, index) => {
          const glyph = character === " " ? "\u00A0" : character;
          return (
            <span
              key={`${character}-${index}`}
              className="relative inline-flex animate-text-hop motion-reduce:animate-none items-baseline"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <span className="pm-daily-nav-letter-cell relative inline-flex items-baseline">
                <span className="pm-daily-nav-letter-face inline-block">{glyph}</span>
                <span className="pm-daily-nav-letter-hover inline-block" aria-hidden="true">
                  {glyph}
                </span>
              </span>
            </span>
          );
        })}
      </span>
    </Link>
  );
}
